import { hasTextBorder } from '../utils';
import {
  hasGradient,
  getHexFromSolid,
  getOpaquePattern,
} from '@web-stories-wp/patterns';
import {
  type DashedTextBorderStyle,
  TextBorderAlignment,
  TextBorderLineCap,
  TextBorderLineJoin,
  type TextBorderStyle,
  TextBorderStyleType,
  type TextElement,
} from '../../types';
import type { CSSProperties } from 'react';

type StrokeProps = CSSProperties & {
  insideStrokeWidth: number;
  outsideStrokeWidth: number;
};

export function getStrokeProps({
  dataToEditorY,
  element,
  strokeGradientId,
}: {
  dataToEditorY: (value: number) => number;
  element: TextElement;
  strokeGradientId: string;
}): StrokeProps {
  const hasBorder = hasTextBorder(element.textBorder);

  if (!hasBorder) {
    return {
      insideStrokeWidth: 0,
      outsideStrokeWidth: 0,
    };
  }

  const pattern = element.textBorder.color;
  // V2 SVG gradients already include overall opacity in their stop colours.
  const strokeOpacity =
    hasGradient(pattern) &&
    'geometryVersion' in pattern &&
    pattern.geometryVersion === 2
      ? 1
      : pattern.color?.a ?? pattern.alpha ?? 1;

  const strokeLinejoin =
    element.textBorder.lineJoin ?? TextBorderLineJoin.Miter;

  const strokeDasharray = isDashedTextBorderStyle(element.textBorder.style)
    ? `${dataToEditorY(element.textBorder.style.dashLength)} ${dataToEditorY(
        element.textBorder.style.gapLength
      )}`
    : undefined;

  const strokeLinecap = element.textBorder.lineCap ?? TextBorderLineCap.Butt;

  const strokeMiterlimit =
    strokeLinejoin === TextBorderLineJoin.Miter
      ? element.textBorder?.miterLimit
      : undefined;

  const insideStrokeWidth =
    element.textBorder.alignment === TextBorderAlignment.Inside
      ? element.textBorder.width
      : element.textBorder.alignment === TextBorderAlignment.Center
      ? element.textBorder.width / 2
      : 0;

  const outsideStrokeWidth =
    element.textBorder.alignment === TextBorderAlignment.Outside
      ? element.textBorder.width
      : element.textBorder.alignment === TextBorderAlignment.Center
      ? element.textBorder.width / 2
      : 0;

  const strokeColor = hasGradient(element.textBorder.color)
    ? `url(#${strokeGradientId})`
    : // Remove opacity from the color so we can use stroke-opacity instead
      `#${getHexFromSolid(
        getOpaquePattern(element.textBorder.color),
        true /** excludeOpacity */
      )}`;

  return {
    insideStrokeWidth,
    outsideStrokeWidth,
    stroke: strokeColor,
    strokeOpacity,
    strokeLinejoin,
    strokeDasharray,
    strokeLinecap,
    strokeMiterlimit,
  };
}

function isDashedTextBorderStyle(
  style: TextBorderStyle
): style is DashedTextBorderStyle {
  return style.type === TextBorderStyleType.Dashed;
}
